'use client';
import { FC, MouseEvent as ReactMouseEvent, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import Image from 'next/image';
import { arrow } from '../assets';
import clsx from 'clsx';
import { useKeyDown } from '@/hooks/use-keydown';
import { createPortal } from 'react-dom';
import { overlayLayers } from '@/ui/overlay-layers';
import { useOverlayPortalContext } from '@/ui/overlay-portal-context';
import { ResolveWidthArgs, useOverlayPosition } from '@/ui/use-overlay-position';
import { SelectDropdown } from '@/ui/select-dropdown';
import { SelectOption, SelectProps } from '@/ui/select.types';

export type { SelectOption, SelectProps } from '@/ui/select.types';

const SELECT_DROPDOWN_VIEWPORT_PADDING = 8;
const SELECT_DROPDOWN_VERTICAL_OFFSET = 4;
const SELECT_DROPDOWN_ESTIMATED_HEIGHT = 240;

const Select: FC<SelectProps> = ({
    options = [],
    value: _value,
    values: _values,
    onChange,
    onValuesChange,
    multiple = false,
    placeholder = 'Выберите вариант',
    disabled,
    className,
    withInput,
    searchString,
    setSearchString,
    uiSize = 'default',
    infoClassName,
    id,
    ariaLabel,
    testId,
}) => {
    const { portalTarget: overlayPortalTarget, isInsideModal } = useOverlayPortalContext();
    const generatedId = useId();
    const resolvedSelectId = id || `select-${generatedId}`;
    const listboxId = `${resolvedSelectId}-listbox`;
    const [value, setValue] = useState(_value);
    const [values, setValues] = useState<string[]>(_values || []);
    const [isMounted, setIsMounted] = useState(false);

    const [isOpen, setIsOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const selectRef = useRef<HTMLDivElement | null>(null);
    const dropdownRef = useRef<HTMLDivElement | null>(null);

    const headerButtonRef = useRef<HTMLButtonElement | null>(null);
    const searchInputRef = useRef<HTMLInputElement | null>(null);

    const currentOption = useMemo(() => {
        if (!value) return;

        return options.find((o) => o.value === value);
    }, [value]);
    const currentOptionPlaceholder = useMemo(() => {
        if (!currentOption) {
            return placeholder;
        }

        return currentOption.info ? `${currentOption.name} ${currentOption.info}` : currentOption.name;
    }, [currentOption, placeholder]);
    const selectedValueSet = useMemo(() => new Set(values), [values]);
    const selectedLabel = useMemo(
        () =>
            options
                .filter((option) => selectedValueSet.has(option.value))
                .map((option) => option.name)
                .join(', '),
        [options, selectedValueSet],
    );

    useEffect(() => {
        setValue(_value);
    }, [_value]);

    useEffect(() => {
        setValues(_values || []);
    }, [_values]);

    useEffect(() => {
        setIsMounted(true);
    }, []);

    useKeyDown({ key: 'Escape', action: () => setIsOpen(false), mountTo: headerButtonRef });

    useKeyDown({
        key: 'ArrowDown',
        action: () => setHighlightedIndex((prev) => (prev < options.length - 1 ? prev + 1 : prev)),
        preventDefault: true,
        mountTo: headerButtonRef,
    });

    useKeyDown({
        key: 'ArrowUp',
        action: () => setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev)),
        preventDefault: true,
        mountTo: headerButtonRef,
    });

    useKeyDown({
        key: 'Enter',
        action: () => {
            if (!isOpen) {
                setIsOpen(true);
                return;
            }

            const option = options[highlightedIndex];
            if (!option) return;

            handleOptionSelect(option);
        },
        preventDefault: true,
        mountTo: headerButtonRef,
        deps: [options, highlightedIndex, isOpen],
    });

    const closeDropdown = useCallback(() => {
        setIsOpen(false);
        setHighlightedIndex(-1);
    }, []);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const onPointerDown = (event: globalThis.MouseEvent | globalThis.TouchEvent) => {
            const target = event.target as Node | null;
            if (!target) {
                return;
            }

            if (selectRef.current?.contains(target)) {
                return;
            }
            if (dropdownRef.current?.contains(target)) {
                return;
            }

            closeDropdown();
        };

        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('touchstart', onPointerDown);

        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('touchstart', onPointerDown);
        };
    }, [closeDropdown, isOpen]);

    useEffect(() => {
        if (isOpen) {
            setHighlightedIndex(-1);
        }
    }, [isOpen]);

    const usesModalPortal = isInsideModal && !!overlayPortalTarget;
    const resolveDropdownWidth = useCallback(({ triggerRect }: ResolveWidthArgs) => triggerRect.width, []);
    const { position: portalPosition } = useOverlayPosition({
        anchorRef: selectRef,
        isOpen,
        enabled: usesModalPortal,
        viewportPadding: SELECT_DROPDOWN_VIEWPORT_PADDING,
        verticalOffset: SELECT_DROPDOWN_VERTICAL_OFFSET,
        estimatedHeight: SELECT_DROPDOWN_ESTIMATED_HEIGHT,
        resolveWidth: resolveDropdownWidth,
    });

    const handleOptionSelect = (option: SelectOption) => {
        if (multiple) {
            const nextValues = values.includes(option.value)
                ? values.filter((currentValue) => currentValue !== option.value)
                : [...values, option.value];
            setValues(nextValues);
            onValuesChange?.(nextValues);
            return;
        }

        setIsOpen(false);
        setHighlightedIndex(-1);
        setValue(option.value);
        onChange?.(option.value);
        setSearchString?.(undefined);
    };

    const onHeaderButtonClick = (event: ReactMouseEvent<HTMLButtonElement>) => {
        if (disabled) return;

        if (withInput && event.target instanceof HTMLInputElement) {
            setIsOpen(true);
            return;
        }

        setIsOpen((prev) => !prev);
        if (withInput) {
            searchInputRef.current?.focus();
        }
    };

    const dropdownLayer = isInsideModal ? overlayLayers.overlayModalPopover : overlayLayers.overlayPopover;
    const dropdownBody = (
        <SelectDropdown
            options={options}
            multiple={multiple}
            value={value}
            selectedValueSet={selectedValueSet}
            highlightedIndex={highlightedIndex}
            usesModalPortal={usesModalPortal}
            portalPosition={portalPosition}
            dropdownLayer={dropdownLayer}
            uiSize={uiSize}
            infoClassName={infoClassName}
            listboxId={listboxId}
            selectId={resolvedSelectId}
            ariaLabel={ariaLabel}
            dropdownRef={dropdownRef}
            onOptionSelect={handleOptionSelect}
            onOptionHover={setHighlightedIndex}
        />
    );

    return (
        <div
            className={clsx(className, 'relative')}
            ref={selectRef}
            data-ui="select"
            data-select-id={resolvedSelectId}
            data-state={isOpen ? 'open' : 'closed'}
            data-testid={testId}
        >
            <button
                type="button"
                className={clsx(
                    `text-left bg-white border rounded-md shadow-sm
                    transition-all duration-200 ease-in-out
                    focus:outline-none focus:ring-blue-500 focus:border-blue-500
                    hover:border-gray-400 w-full`,
                    uiSize === 'default' && 'px-4 py-1.5 text-sm leading-5 min-h-[40px]',
                    uiSize === 'compact' && 'px-3 py-1 text-xs leading-4 min-h-[32px]',

                    disabled
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200'
                        : 'border-slate-200 cursor-pointer',
                )}
                onClick={onHeaderButtonClick}
                ref={headerButtonRef}
                disabled={disabled}
                aria-controls={listboxId}
                aria-expanded={isOpen}
                aria-haspopup="listbox"
                aria-label={ariaLabel || placeholder}
                data-select-trigger={resolvedSelectId}
                data-state={isOpen ? 'open' : 'closed'}
            >
                {withInput ? (
                    <input
                        id={`${resolvedSelectId}-search`}
                        ref={searchInputRef}
                        className={clsx(
                            'outline-none border-none w-full h-full bg-transparent',
                            uiSize === 'default' && 'text-sm leading-5',
                            uiSize === 'compact' && 'text-xs leading-4',
                        )}
                        value={searchString || ''}
                        onChange={(e) => setSearchString?.(e.target.value)}
                        placeholder={currentOptionPlaceholder}
                        aria-label={ariaLabel ? `${ariaLabel}: поиск` : `${placeholder}: поиск`}
                        data-select-search={resolvedSelectId}
                    />
                ) : (
                    <div className="flex items-center justify-between">
                        <span
                            className={clsx(
                                'truncate',
                                multiple
                                    ? values.length
                                        ? 'text-slate-900'
                                        : 'text-slate-400'
                                    : value
                                      ? 'text-slate-900'
                                      : 'text-slate-400',
                            )}
                        >
                            {multiple
                                ? values.length
                                    ? selectedLabel
                                    : placeholder
                                : value
                                  ? currentOption?.name
                                  : placeholder}
                        </span>
                        <Image
                            src={arrow}
                            key={'select-arrow'}
                            alt="select-arrow"
                            width={uiSize === 'compact' ? 14 : 16}
                            height={uiSize === 'compact' ? 14 : 16}
                            className={clsx(isOpen ? 'rotate-270' : 'rotate-90', 'transition-transform')}
                        />
                    </div>
                )}
            </button>

            {isOpen &&
                (usesModalPortal && isMounted && overlayPortalTarget
                    ? createPortal(dropdownBody, overlayPortalTarget)
                    : dropdownBody)}
        </div>
    );
};

export default Select;
